/**
 * The weather, drawn rather than spelled.
 *
 * Two reasons these are hand-drawn SVG and not emoji. The app is a scorecard —
 * ink on paper, one typeface, two colors — and a color emoji dropped into
 * that reads as somebody else's app pasted in. And emoji are rendered by the
 * phone, so the same forecast is a different picture on an iPhone and an
 * Android, which is not a thing a scorecard does.
 *
 * The arrow is the one that earns its keep. "Wind 18 N" is four characters a
 * man has to translate into a picture before it means anything on the 4th tee;
 * an arrow already IS the picture. It points the way a weather vane points —
 * at the quarter the wind is coming from — so the arrow and the letter beside
 * it say the same thing, which is what "the wind direction" means to everybody
 * who is not writing the code.
 *
 * Everything inherits `currentColor` and sizes off the text around it, so one
 * glyph works on the dark day header and on the paper below it.
 */

import { compass } from '../engine/weather'

/** Shared frame: 16×16, no fill, strokes that match the app's rules. */
const svg = {
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  className: 'inline-block shrink-0',
  width: 16,
  height: 16,
  'aria-hidden': true,
}

const Sun = () => (
  <svg {...svg}>
    <circle cx="8" cy="8" r="3.2" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
      <line
        key={a}
        x1={8 + 5 * Math.cos((a * Math.PI) / 180)}
        y1={8 + 5 * Math.sin((a * Math.PI) / 180)}
        x2={8 + 6.8 * Math.cos((a * Math.PI) / 180)}
        y2={8 + 6.8 * Math.sin((a * Math.PI) / 180)}
      />
    ))}
  </svg>
)

/** A cloud, optionally with the sun behind its shoulder. */
const Cloud = ({ withSun, below }: { withSun?: boolean; below?: 'rain' | 'drizzle' | 'snow' | 'bolt' }) => (
  <svg {...svg}>
    {withSun && (
      <>
        <circle cx="5" cy="5" r="2.2" />
        <line x1="5" y1="0.8" x2="5" y2="1.9" />
        <line x1="1.4" y1="5" x2="2.4" y2="5" />
        <line x1="2.3" y1="2.3" x2="3.1" y2="3.1" />
      </>
    )}
    <path d="M4.6 12.2h6.6a2.6 2.6 0 0 0 .3-5.2 3.6 3.6 0 0 0-6.9-.6 2.4 2.4 0 0 0 0 5.8Z" />
    {below === 'rain' && (
      <>
        <line x1="5.6" y1="13.6" x2="5" y2="15.4" />
        <line x1="8.2" y1="13.6" x2="7.6" y2="15.4" />
        <line x1="10.8" y1="13.6" x2="10.2" y2="15.4" />
      </>
    )}
    {below === 'drizzle' && (
      <>
        <line x1="6.2" y1="13.8" x2="5.9" y2="15" />
        <line x1="9.6" y1="13.8" x2="9.3" y2="15" />
      </>
    )}
    {below === 'snow' && (
      <>
        <line x1="5.4" y1="14.4" x2="6.6" y2="14.4" />
        <line x1="6" y1="13.8" x2="6" y2="15" />
        <line x1="9.4" y1="14.4" x2="10.6" y2="14.4" />
        <line x1="10" y1="13.8" x2="10" y2="15" />
      </>
    )}
    {below === 'bolt' && <path d="M8.6 13.2 7 15.6h2l-1.4 2" />}
  </svg>
)

const Fog = () => (
  <svg {...svg}>
    <path d="M4.6 10.4h6.6a2.6 2.6 0 0 0 .3-5.2 3.6 3.6 0 0 0-6.9-.6 2.4 2.4 0 0 0 0 5.8Z" />
    <line x1="2.6" y1="12.6" x2="13.4" y2="12.6" />
    <line x1="4.4" y1="14.8" x2="11.6" y2="14.8" />
  </svg>
)

/**
 * What the sky is doing, as a mark.
 *
 * Returns nothing for a code it does not know, exactly as `conditions` returns
 * an empty string — a shrug is honest, a wrong picture of the weather is not.
 */
export function SkyGlyph({ code }: { code: number | undefined }) {
  if (code === undefined) return null
  if (code === 0) return <Sun />
  if (code === 1 || code === 2) return <Cloud withSun />
  if (code === 3) return <Cloud />
  if (code === 45 || code === 48) return <Fog />
  if (code >= 51 && code <= 57) return <Cloud below="drizzle" />
  if (code >= 61 && code <= 67) return <Cloud below="rain" />
  if (code >= 71 && code <= 77) return <Cloud below="snow" />
  if (code >= 80 && code <= 82) return <Cloud below="rain" />
  if (code >= 95) return <Cloud below="bolt" />
  return null
}

/**
 * The direction of the wind, and how hard.
 *
 * Points the way a weather vane points: at the quarter the wind is coming from,
 * so a north wind reads as an arrow to the north and the letter beside it says
 * N. Scott's call, and it is the reading that matches what everybody means by
 * "the wind direction" — the first version pointed the way the ball gets
 * pushed, which is a different true thing and not the one being asked for.
 */
export function WindArrow({ deg, strong }: { deg: number; strong?: boolean }) {
  return (
    <svg
      {...svg}
      style={{ transform: `rotate(${deg % 360}deg)` }}
      strokeWidth={strong ? 2 : 1.5}
      role="img"
      aria-hidden={undefined}
      aria-label={`Wind out of the ${compass(deg)}`}
    >
      <line x1="8" y1="13.5" x2="8" y2="2.5" />
      <path d="M4.6 6 8 2.5 11.4 6" />
    </svg>
  )
}
